import { ISessionManager, SessionSetupFunction } from '../../interfaces/core/ISessionManager';
import { ITestSession, TestSessionOptions } from '../../interfaces/core/ITestSession';
import { ISessionRepository } from '../../interfaces/core/ISessionRepository';
import { IEventBus } from '../../interfaces/events/IEventBus';
import { TestSession } from './TestSession';

export class SessionManager implements ISessionManager {
  constructor(
    private repository: ISessionRepository,
    private eventBus: IEventBus
  ) {}

  async createSession(
    label: string, 
    setupFn: SessionSetupFunction, 
    options?: Partial<TestSessionOptions>
  ): Promise<ITestSession> {
    if (!label || typeof label !== 'string') {
      throw new Error('Session label must be a non-empty string');
    }
    
    if (this.exists(label)) {
      throw new Error(`Session with label '${label}' already exists`);
    }

    const sessionId = TestSession.createId(label);
    const mergedOptions = { ...TestSession.getDefaultOptions(), ...options };
    const session = new TestSession(sessionId, label, mergedOptions);

    // Store setup function for later execution
    (session as any).setupFn = setupFn;

    try {
      // Execute setup function
      const result = setupFn();
      if (result instanceof Promise) {
        await result;
      }
      
      this.repository.save(session);
      
      this.eventBus.emit({
        type: 'session:created',
        sessionLabel: label,
        timestamp: new Date().toISOString(),
        data: { sessionId, label, options: mergedOptions }
      });

      return session;
    } catch (error) {
      session.setError(error as Error);
      
      this.eventBus.emit({
        type: 'session:creation-failed',
        sessionLabel: label,
        timestamp: new Date().toISOString(),
        data: { sessionId, label, error: (error as Error).message }
      });
      
      throw error;
    }
  }

  getSession(label: string): ITestSession | null {
    return this.repository.findByLabel(label);
  }

  getAllSessions(): ITestSession[] {
    return this.repository.findAll();
  }

  deleteSession(label: string): boolean {
    const session = this.getSession(label);
    if (session) {
      const deleted = this.repository.delete(session.id);
      if (deleted) {
        this.eventBus.emit({
          type: 'session:deleted',
          sessionLabel: label,
          timestamp: new Date().toISOString(),
          data: { sessionId: session.id, label }
        });
      }
      return deleted;
    }
    return false;
  }

  exists(label: string): boolean {
    return this.repository.findByLabel(label) !== null;
  }

  clear(): void {
    this.repository.clear();
  }
}
